import { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import classNames from 'classnames';
import ProductImageEditor from '@pages/ai-product/image-editor/ProductImageEditor';
import ProductImageFormDialog from '@pages/ai-product/ProductImageFormDialog';
import {
  processAiProduct, renderAiProduct,
  updateAiProduct,
} from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import EditIcon from '@resources/icons/EditIcon';
import LimitBanner from '@shared/components/page-parts/LimitBanner';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    gap: '20px',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '20px',
    border: '1px solid #00C98F',
    borderRadius: '4px',
  },
  uploadImage: {
    width: '268px',
    height: '184px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #E3E9F1',
    flex: '0 0 268px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  siloImage: {
    '& > img': {
      objectFit: 'contain',
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '95%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    margin: '50px 0 16px 0',
    gap: '16px',
    '& > button': {
      minWidth: '140px',
      width: '100%',
    },
  },
  chooseImageEmpty: {
    objectFit: 'cover',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '268px',
    height: '184px',
    border: '1px solid #E3E9F1',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  uploadVideo: {
    width: '268px',
    position: 'relative',
    display: 'flex',
    height: '184px',
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  iconButton: {
    cursor: 'pointer',
    display: 'flex',
    border: '1px solid #E3E9F1',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    height: 'fit-content',
    borderRadius: '50%',
    padding: '4px',
    '& > svg': {
      color: '#9DA3B2',
    },
  },
  reviewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  reviewHeader: {
    fontSize: '16px',
    fontWeight: 700,
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
    marginBottom: '15px',
    flexDirection: 'row',
    gap: '20px',
  },
  uploadText: {
    display: 'inline-flex',
    flexDirection: 'column',
    padding: '15px',
    border: '1px solid #E3E9F1',
    borderRadius: '4px',
    width: '368px',
    height: '135px',

    '& > p': {
      overflowY: 'auto',
      fontSize: '14px',
      color: '#272D4E',
    },
  },
}));

export function ReviewStep() {
  const classes = useStyles();
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const product = useSelector(selectAiProduct);
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  return (
    <div className={ classes.pageWrapper }>
      <div className={ classes.header }>
        Product Detail Page Content Pack
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
            Step 4. Review
          <div className={ classes.contentSubHeader }>
            Review all created content and confirm it is ready. Then you can download it.
          </div>
        </div>
        {product.images && product.images.some((image) => image.siloSrc) && (
        <div className={ classes.reviewWrapper }>
          <div className={ classes.reviewHeader }>
            SILO-Photos
          </div>
          <div className={ classes.contentWrapper }>
            {product.images.map((image) => {
              return (
                <div
                  key={ `${image.id}` }
                  className={ classNames(classes.uploadImage, classes.siloImage) }
                >
                  <img src={ image.siloSrc } />
                </div>
              );
            })}
            <div
              className={ classes.iconButton }
              onClick={ () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.SILO(product.id)}`);
              } }
            >
              <EditIcon />
            </div>
          </div>
        </div>
        )}
        {product.images?.some((image) => image.lifestyle?.length > 0) && (
        <div className={ classes.reviewWrapper }>
          <div className={ classes.reviewHeader }>
            Lifestyle-Photos
          </div>
          <div className={ classes.contentWrapper }>
          { product.images.map((image) => {
            return (
              <>
                {!!image.lifestyle?.length && image.lifestyle.filter((lifestyleItem) => lifestyleItem.selected).map((lifestyle, index) => {
                  return (
                    <div
                      key={ `${image.id}${index}` }
                      className={ classes.uploadImage }
                    >
                      <img src={ lifestyle.src } />
                    </div>
                  );
                })}
              </>
            );
          })}
            <div
              className={ classes.iconButton }
              onClick={ () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.BACKGROUND(product.id)}`);
              } }
            >
              <EditIcon />
            </div>
          </div>
        </div>
        )}
      {product.images?.some((image) => image.lifestyle?.some((lifestyleItem) => lifestyleItem.videoSrc && lifestyleItem.isVideoUsed)) && (
        <div className={ classes.reviewWrapper }>
          <div className={ classes.reviewHeader }>
            Video
          </div>
          <div className={ classes.contentWrapper }>
            {product.images.map((image) => {
              return (
                <>
                  {!!image.lifestyle?.length && image.lifestyle
                    .filter((lifestyleItem) => lifestyleItem.isVideoUsed && lifestyleItem.videoSrc)
                    .map((lifestyle, index) => {
                      return (
                        <div
                          key={ `${image.id}${index}` }
                          className={ classes.uploadVideo }
                        >
                          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                          <video width="100%" autoPlay playsInline loop muted>
                            <source src={ lifestyle.videoSrc } type="video/mp4" />
                            Your browser does not support the video.
                          </video>
                        </div>
                      );
                    })}
                </>
              );
            })}
            <div
              className={ classes.iconButton }
              onClick={ () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.VIDEO(product.id)}`);
              } }
            >
              <EditIcon />
            </div>
          </div>
        </div>
      )}
        {product.texts && product.texts.some((text) => text.selected) && (
        <div className={ classes.reviewWrapper }>
          <div className={ classes.reviewHeader }>
            Product Description
          </div>
          <div className={ classes.contentWrapper }>
            {product.texts
              .filter((text) => text.selected)
              .map((text) => (
                <div key={ text.id } className={ classes.uploadText }>
                  <p>{ text.text }</p>
                </div>
              ))}
            <div
              className={ classes.iconButton }
              onClick={ () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
              } }
            >
              <EditIcon />
            </div>
          </div>
        </div>
        )}
        <div className={ classes.actions }>
          <Button
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.DOWNLOAD(product.id)}`);
              }));
            } }
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
